'use client';

import { useBoatFavorite } from '@hooks';
import { PublicBoatCard, type PublicBoatCardProps } from '@marine/boats/src/components/PublicBoatCard';

/**
 * A `PublicBoatCard` that owns its own favorite toggle.
 *
 * It exists because the toggle needs an `onFavoriteClick` function and the lists that render these
 * cards are server components, which cannot pass one. Wrapping here rather than adding
 * `'use client'` to each list keeps the lists on the server, where their Sanity data already is.
 *
 * The favorite props are deliberately not forwardable: a caller passing its own `isFavorite` or
 * `onFavoriteClick` would be a second source of truth for the same boat.
 */
export type FavoritablePublicBoatCardProps = Omit<
  PublicBoatCardProps,
  'isFavorite' | 'onFavoriteClick' | 'showFavoriteToggle' | 'disableFavoriteButton'
> & {
  /** The boat's Sanity document id — `_id`, which is what a favorite is keyed on. */
  sanityBoatId: string;
};

export const FavoritablePublicBoatCard = ({ sanityBoatId, ...cardProps }: FavoritablePublicBoatCardProps) => {
  const { showToggle, isFavorited, isToggling, toggle } = useBoatFavorite(sanityBoatId);

  return (
    <PublicBoatCard
      {...cardProps}
      boatId={sanityBoatId}
      showFavoriteToggle={showToggle}
      isFavorite={isFavorited}
      disableFavoriteButton={isToggling}
      onFavoriteClick={toggle}
    />
  );
};
